/*
 * Quick Entry section.
 *
 * Lets a shopper pick a price "tier" (each tied to a product), see the giveaway
 * entries they'll earn, choose a quantity, then add-to-cart and open the cart drawer.
 *
 * Entry math is authored in Liquid (data-entries = multiplier * price / 100) so
 * the number shown to the shopper is byte-for-byte the number attached as the
 * line-item property. Because this flow adds to cart with fetch (not the theme's
 * normal cart:update pipeline), we re-sum and persist the cart-level entries
 * attribute ourselves (mirrors assets/sync-cart-entries.js), then refresh and
 * open the cart drawer via the Section Rendering API (falling back to /cart).
 */
(() => {
  const toInt = (value) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? 0 : n;
  };

  function initSection(root) {
    if (!root || root.dataset.qeInitialized === 'true') return;
    root.dataset.qeInitialized = 'true';

    const tierButtons = Array.from(root.querySelectorAll('[data-tier]'));
    const visuals = Array.from(root.querySelectorAll('.quick-entry__visual'));
    if (!tierButtons.length) return;

    const qtyInput = root.querySelector('[data-qty-input]');
    const qtyDisplay = root.querySelector('[data-qty-display]');
    const minusBtn = root.querySelector('[data-qty-minus]');
    const plusBtn = root.querySelector('[data-qty-plus]');
    const ctaButton = root.querySelector('[data-buy-button]');
    const buyVariant = root.querySelector('[data-buy-variant-id]');
    const buyQty = root.querySelector('[data-buy-quantity]');

    const multEl = root.querySelector('[data-active-multiplier]');
    const entriesValueEl = root.querySelector('[data-entries-value]');
    const priceEl = root.querySelector('[data-active-price]');
    const descEl = root.querySelector('[data-active-description]');

    const giveawayName =
      root.dataset.giveawayName ||
      (window.entries && window.entries.giveaway_name) ||
      '';

    const tierData = (el) => ({
      el,
      idx: el.dataset.svgIndex,
      priceCents: toInt(el.dataset.priceCents),
      priceMoney: el.dataset.priceMoney || '',
      variantId: el.dataset.variantId || '',
      tickets: el.dataset.tickets || '',
      multiplier: toInt(el.dataset.multiplier),
      entries: toInt(el.dataset.entries),
      description: el.dataset.description || '',
    });

    let active = tierData(tierButtons[0]);

    const getQty = () => Math.max(1, toInt(qtyInput && qtyInput.value) || 1);

    // Per-unit entries: prefer the Liquid-computed value so the UI and the
    // line-item property can never drift apart.
    const entriesPerUnit = () =>
      active.entries || Math.round(active.multiplier * (active.priceCents / 100));

    function recalc() {
      const qty = getQty();
      const total = qty * entriesPerUnit();
      if (entriesValueEl) entriesValueEl.textContent = total.toLocaleString();
      if (buyQty) buyQty.value = String(qty);
      if (buyVariant) buyVariant.value = active.variantId;
      if (qtyDisplay) qtyDisplay.textContent = String(qty);
    }

    function setActive(tierEl) {
      active = tierData(tierEl);

      tierButtons.forEach((b) => b.classList.toggle('is-active', b === tierEl));
      visuals.forEach((v) =>
        v.classList.toggle('is-active', v.dataset.svgIndex === active.idx),
      );

      if (priceEl) priceEl.textContent = active.priceMoney;
      if (descEl) descEl.textContent = active.description;
      if (multEl) multEl.textContent = active.multiplier + 'X';

      recalc();
    }

    function setQty(next) {
      if (qtyInput) qtyInput.value = String(Math.max(1, next));
      recalc();
    }

    function setLoading(isLoading) {
      root.classList.toggle('is-loading', isLoading);
      if (ctaButton) {
        ctaButton.disabled = isLoading;
        ctaButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    // Re-sum properties[key] across ALL cart lines and persist as a cart
    // attribute. Non-digit chars (e.g. thousands separators) are stripped.
    async function syncCartAttribute(key) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();

        let total = 0;
        (cart.items || []).forEach((item) => {
          const raw = item.properties && item.properties[key];
          if (raw == null) return;
          const perUnit = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
          if (!Number.isNaN(perUnit)) total += perUnit * item.quantity;
        });

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [key]: String(total) } }),
        });
      } catch (error) {
        console.error('[quick-entry] cart attribute sync failed:', error);
      }
    }

    // Refresh the cart drawer from the server and slide it open. Mirrors Dawn's
    // product-form.js -> CartDrawer.renderContents() pattern, but pulls the
    // sections with a standalone GET so the markup reflects the entries
    // attribute we sync AFTER the add. Falls back to the /cart page if this
    // storefront has no drawer.
    async function openCart(variantId) {
      const cartDrawer = document.querySelector('cart-drawer');

      if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
        window.location.href = '/cart';
        return;
      }

      try {
        const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
        const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`sections request failed: ${res.status}`);
        const sections = await res.json();
        // The host <cart-drawer> keeps the `is-empty` class it was rendered with
        // at page load; renderContents only clears it on `.drawer__inner`. If the
        // cart started empty that class would hide the freshly-added line item,
        // so drop it here too (matches assets/product-form.js).
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents({ id: variantId, sections });
      } catch (error) {
        console.error('[quick-entry] open cart drawer failed:', error);
        window.location.href = '/cart';
      }
    }

    async function addToCartAndOpen() {
      if (!active || !active.variantId) return;

      const qty = getQty();
      const item = { id: toInt(active.variantId), quantity: qty };

      const perUnit = entriesPerUnit();
      if (giveawayName && perUnit > 0) {
        item.properties = { [giveawayName]: String(perUnit) };
      }

      setLoading(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });

        if (!res.ok) {
          let message = 'Failed to add item to cart.';
          try {
            const data = await res.json();
            message = data.description || message;
          } catch (_) {
            /* non-JSON error body */
          }
          throw new Error(message);
        }

        if (giveawayName) await syncCartAttribute(giveawayName);

        await openCart(active.variantId);
      } catch (error) {
        console.error('[quick-entry] add to cart failed:', error);
      } finally {
        setLoading(false);
      }
    }

    tierButtons.forEach((el) => {
      el.addEventListener('click', () => setActive(el));
    });
    if (minusBtn) minusBtn.addEventListener('click', () => setQty(getQty() - 1));
    if (plusBtn) plusBtn.addEventListener('click', () => setQty(getQty() + 1));
    if (ctaButton) {
      ctaButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        addToCartAndOpen();
      });
    }

    // Sync everything to the initial (first) tier.
    setActive(tierButtons[0]);
  }

  function boot() {
    document.querySelectorAll('[data-quick-entry]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when a section is (re)loaded.
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!target) return;
    if (target.matches && target.matches('[data-quick-entry]')) {
      initSection(target);
    } else {
      target.querySelectorAll('[data-quick-entry]').forEach(initSection);
    }
  });
})();
